import React, {
    forwardRef,
    Fragment,
    useCallback,
    useContext,
    useEffect,
    useRef,
    useState
} from 'react';
import AsDropdownTongue from './AsDropdownTongue';
import { AsDropdownContext } from './AsDropdownContext';
import {
    AsLabel,
    Flex,
    IconChevron,
    RadioButton,
    Spacer,
    Tickbox
} from '@adserve/adserve-react-components';

const AsDropdownInput = forwardRef(
    (
        {
            activeValues = [],
            disabled = false,
            disabledStyle,
            disabledValues = [],
            filterDisabledValues = false,
            height = 'max-content',
            id = 'as-dropdown',
            containerStyle,
            inputStyle,
            labelStyle,
            tongueStyle,
            textStyle,
            name,
            onApply,
            onChange,
            onCheck,
            onClose,
            onOpen,
            onTick,
            style,
            title,
            value,
            values,
            width,
            transformChevron,
            children,
            placeholder
        },
        ref
    ) => {
        const localRef = useRef(null);
        // ref is used by the tongue, so we must use localRef if a ref hasn't been passed in
        const boundingRef = ref || localRef;

        const { isExpanded, setHeight, setIsExpanded } =
            useContext(AsDropdownContext);

        useEffect(
            useCallback(() => {
                /* Starts with the tongue expanded, so we can quickly
                 * measure everything. Then retract, before the user notices! */
                setIsExpanded(false);
                const clickOutEvent = (event) => {
                    if (
                        !boundingRef.current?.contains(event.target) &&
                        isExpanded &&
                        event.target.nodeName !== 'HTML'
                    ) {
                        setIsExpanded(false);
                    }
                };
                document.addEventListener('click', clickOutEvent);
                return () =>
                    document.removeEventListener('click', clickOutEvent);
            }, [isExpanded]),
            []
        );

        useEffect(() => {
            setHeight(height);
        }, [height]);

        const [highlighted, setHighlighted] = useState();

        labelStyle = {
            color: '#414141',
            display: 'block',
            fontFamily: 'roboto',
            fontSize: 12,
            textAlign: 'left',
            ...style,
            ...labelStyle
        };

        disabledStyle = {
            backgroundColor: '#f7f7f7',
            cursor: 'default',
            opacity: 1,
            ...disabledStyle
        };

        inputStyle = {
            boxSizing: 'border-box',
            height: 40,
            width: '100%',
            padding: 12,
            fontFamily: 'roboto',
            fontSize: 12,
            textAlign: 'center',
            border: '1px solid #d7d7d7',
            borderRadius: 5,
            alignItems: 'center',
            backgroundColor: '#fffdf4',
            color: '#414141',
            cursor: 'pointer',
            position: 'relative',
            userSelect: 'none',
            KhtmlUserSelect: 'none',
            MsUserSelect: 'none',
            MozUserSelect: 'none',
            WebkitUserSelect: 'none',
            zIndex: isExpanded ? 100 : undefined,
            ...(disabled ? disabledStyle : {}),
            ...style,
            ...inputStyle
        };

        /**
         * Event handler for a dropdown box click
         * @param {Event} event - The triggering event
         */
        const onClick = (event) => {
            // Prevents the body click event from firing and  immediately closing.
            if (disabled || values.length <= 1) return;
            if (typeof onClose === 'function' && isExpanded) {
                setHighlighted(undefined);
                onClose();
            } else if (typeof onOpen === 'function' && !isExpanded) {
                onOpen();
            }

            setIsExpanded(!isExpanded);
        };

        /**
         * Event handler for an option click
         * @param {Event} event - The triggering event
         * @param {Object} element - The selected element
         */
        const onOptionClick = (event, element) => {
            event.preventDefault();
            if (disabledValues.some((dv) => dv === element)) return;
            if (typeof onTick === 'function') {
                event.stopPropagation();
                onTick(element, event);
            } else if (typeof onCheck === 'function') {
                onCheck(element, event);
            }
            if (typeof onChange !== 'function') return;
            onChange(element, event);
            setIsExpanded(false);
        };

        /**
         * Event handler for mouse over/mouse out
         * @param {Event} event - The triggering event
         * @param {Object} element - The selected element
         */
        const onHover = (event, element) => {
            event.preventDefault();
            setHighlighted(element);
        };

        const handleApply = useCallback(
            (event) => {
                const appliedValues = filterDisabledValues
                    ? activeValues.filter((av) => !disabledValues.includes(av))
                    : activeValues;
                onApply(appliedValues, event);
                setIsExpanded(false);
            },
            [onApply, activeValues]
        );

        return (
            <div
                id={id}
                ref={boundingRef}
                style={{ width: width, ...containerStyle }}
                onClick={onClick}
            >
                <AsLabel style={labelStyle} htmlFor={name}>
                    {title}
                </AsLabel>

                {children || (
                    <Flex spaceBetween style={inputStyle}>
                        <span
                            style={{
                                color: '#414141',
                                fontWeight: 500,
                                fontSize: 14,
                                ...textStyle
                            }}
                        >
                            {typeof value === 'string'
                                ? value
                                : value?.name || placeholder}
                        </span>

                        {values.length > 1 && (
                            <IconChevron
                                width={11}
                                style={{
                                    boxSizing: 'border-box',
                                    pointerEvents: 'none',
                                    transform:
                                        transformChevron && isExpanded
                                            ? 'rotate(180deg)'
                                            : 'rotate(0deg)'
                                }}
                            />
                        )}
                    </Flex>
                )}
                <AsDropdownTongue
                    boundingRef={boundingRef}
                    expanded={isExpanded}
                    onApply={
                        typeof onApply === 'function' ? handleApply : undefined
                    }
                    onClose={onClick}
                    style={tongueStyle}
                >
                    {values.map((element, i) =>
                        element?.id === undefined ? (
                            <Fragment key={i}>
                                <Spacer height={14} />

                                <span
                                    style={{
                                        margin: 11,
                                        padding: 6,
                                        height: 30,
                                        alignItems: 'center',
                                        color: '#414141',
                                        fontSize: 14,
                                        fontWeight: 500,
                                        ...element?.style
                                    }}
                                >
                                    {element?.name}
                                </span>
                            </Fragment>
                        ) : (
                            <div
                                key={i}
                                onClick={(event) =>
                                    onOptionClick(event, element)
                                }
                                onMouseOver={(event) => onHover(event, element)}
                                onMouseLeave={onHover}
                                style={{
                                    display: 'flex',
                                    boxSizing: 'border-box',
                                    backgroundColor:
                                        element === highlighted && // Is hovered over
                                        value !== element && // Is not selected
                                        !disabledValues.some(
                                            (dv) => dv === element
                                        ) // Is not disabled
                                            ? '#dcffff'
                                            : '#ffffff',
                                    padding: '0 6px',
                                    cursor:
                                        element === highlighted &&
                                        value !== element &&
                                        !disabledValues.some(
                                            (dv) => dv === element
                                        )
                                            ? 'pointer'
                                            : 'default',
                                    opacity:
                                        (typeof onCheck !== 'function' &&
                                            value === element &&
                                            typeof onTick !== 'function') ||
                                        disabledValues.some(
                                            (dv) => dv === element
                                        )
                                            ? 0.5
                                            : 1,
                                    height: 25,
                                    alignItems: 'center',
                                    color: '#414141',
                                    fontSize: 14,
                                    fontWeight: 500
                                }}
                            >
                                {(typeof onTick === 'function' && (
                                    <Tickbox
                                        value={
                                            activeValues.some(
                                                (av) =>
                                                    av?.id === element?.id ||
                                                    av === element
                                            ) &&
                                            !disabledValues.some(
                                                (dv) => dv === element
                                            )
                                        }
                                        disabled={disabledValues.some(
                                            (dv) =>
                                                dv?.id === element?.id ||
                                                dv === element
                                        )}
                                    />
                                )) ||
                                    (typeof onCheck === 'function' && (
                                        <RadioButton
                                            value={
                                                value?.id === element?.id ||
                                                value === element
                                            }
                                        />
                                    ))}
                                <span
                                    style={{
                                        marginLeft: 11,
                                        pointerEvents: 'none',
                                        ...element?.style
                                    }}
                                >
                                    {element?.name}
                                </span>
                            </div>
                        )
                    )}
                </AsDropdownTongue>
            </div>
        );
    }
);

AsDropdownInput.displayName = 'AsDropdownInput';

AsDropdownInput.defaultProps = {
    filterDisabledValues: false,
    disabled: false,
    id: 'as-dropdown'
};

export default AsDropdownInput;
